import type { OrganizationItem, OrganizationMember } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import {
  RiEdit2Line,
  RiDeleteBinLine,
  RiTeamLine,
  RiGlobalLine,
  RiDownloadLine,
  RiCoinsLine
} from '@remixicon/react';
import {
  Button,
  Tooltip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, TableFilters, ColumnsToggleDropdown } from '@/components/table';
import {
  organizationsListParamsAtom,
  organizationsQueryAtom,
  deletedOrganizationsListParamsAtom,
  deleteOrganization,
  getOrganizationMembers,
  fetchOrganizationDomains as fetchOrganizationDomainsAtom
} from '@/store';
import { useTableExport } from '@/components/table/useTableExport';
import { ExportConfirmModal } from '@/components/table/ExportConfirmModal';

// Custom hooks
import { useOrganizationsFilters, useOrganizationsSorting } from '@/hooks/organizations';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { MembersModal } from '@/components/organizations/MembersModal';
import { DomainsModal } from '@/components/organizations/DomainsModal';
import { EditOrganizationModal } from '@/components/organizations/EditOrganizationModal';
import { AdjustTokensModal } from '@/components/organizations/AdjustTokensModal';
import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { SearchInput } from '@/components/shared/SearchInput';
import { OrganizationCell } from '@/components/shared/OrganizationCell';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';

export default function OrganizationsPage() {
  const { data, isLoading, isError, error } = useAtomValue(organizationsQueryAtom);
  const [params, setParams] = useAtom(organizationsListParamsAtom);
  const [, setDeletedParams] = useAtom(deletedOrganizationsListParamsAtom);

  const organizations = React.useMemo<OrganizationItem[]>(() => data?.data ?? [], [data]);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    hasActiveFilters,
    handleSearch,
    handleClearSearch,
    clearFilters,
    handlePageChange,
    dateFilter,
    handleDateChange
  } = useOrganizationsFilters();

  const { sortDescriptor, setSortDescriptor, sortedItems } = useOrganizationsSorting(organizations);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Modal states
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onOpenChange: onDeleteOpenChange } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onOpenChange: onEditOpenChange } = useDisclosure();
  const { isOpen: isTokensOpen, onOpen: onTokensOpen, onOpenChange: onTokensOpenChange } = useDisclosure();
  const [isMembersOpen, setIsMembersOpen] = React.useState(false);
  const [selectedOrgId, setSelectedOrgId] = React.useState<number | null>(null);
  const [selectedOrg, setSelectedOrg] = React.useState<any | null>(null);
  const [selectedOrgName, setSelectedOrgName] = React.useState<string>('');
  const [members, setMembers] = React.useState<OrganizationMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = React.useState(false);
  const [isDomainsOpen, setIsDomainsOpen] = React.useState(false);
  const [domains, setDomains] = React.useState<any[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [, fetchOrganizationDomains] = useAtom(fetchOrganizationDomainsAtom);

  // Load members
  const loadMembers = React.useCallback(async (orgId: number, orgName: string) => {
    // First, open modal immediately and set loading state
    setSelectedOrgId(orgId);
    setSelectedOrgName(orgName);
    setMembers([]); // Clear previous members
    setIsLoadingMembers(true);
    setIsMembersOpen(true);

    // Wait a bit to ensure modal is open before API call
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Then fetch members
    try {
      const membersData = await getOrganizationMembers(orgId);
      setMembers(membersData);
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsLoadingMembers(false);
    }
  }, []);

  // Load domains
  const loadDomains = React.useCallback(async (orgId: number, orgName: string) => {
    // First, open modal immediately and set loading state
    setSelectedOrgId(orgId);
    setSelectedOrgName(orgName);
    setDomains([]); // Clear previous domains immediately
    setIsLoadingDomains(true);
    setIsDomainsOpen(true);

    // Wait a bit to ensure modal is open before API call
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Then fetch domains via atomized API service
    try {
      const domainsData = await fetchOrganizationDomains({ organizationId: orgId });
      setDomains(domainsData || []);
    } catch (error) {
      // Error handled by axios interceptor
      setDomains([]); // Clear domains on error
    } finally {
      setIsLoadingDomains(false);
    }
  }, []);

  // Delete organization
  const handleDelete = React.useCallback(async () => {
    if (!selectedOrgId) return;

    try {
      setIsDeleting(true);
      await deleteOrganization(selectedOrgId);
      triggerRefresh();
      // Also refresh deleted organizations cache
      setDeletedParams((prev) => ({ ...prev, refresh: Date.now() }));
      onDeleteOpenChange();
      setSelectedOrgId(null);
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsDeleting(false);
    }
  }, [selectedOrgId, triggerRefresh, setDeletedParams, onDeleteOpenChange]);

  // Using shared DateTimeDisplay for date rendering

  const renderCell = React.useCallback(
    (organization: OrganizationItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name': {
          return <OrganizationCell organization={organization} showDescription />;
        }
        case 'token_balance':
          return (
            <span className="font-mono text-sm" title={i18n.t('copy.available_event_quota_events')}>
              {(organization.token_balance ?? 0).toLocaleString()}
            </span>
          );
        case 'user':
          return organization.user ? <UserCell user={organization.user} linked /> : <span>-</span>;
        case 'created_at':
          return <DateTimeDisplay dateString={organization.created_at} />;
        case 'members':
          return (
            <Button
              color="primary"
              size="sm"
              variant="flat"
              onPress={() => {
                loadMembers(organization.id, organization.name);
              }}
              startContent={<RiTeamLine className="h-4 w-4" />}
            >{i18n.t('copy.members')}</Button>
          );
        case 'domains':
          return (
            <Button
              color="secondary"
              size="sm"
              variant="flat"
              onPress={() => {
                loadDomains(organization.id, organization.name);
              }}
              startContent={<RiGlobalLine className="h-4 w-4" />}
            >{i18n.t('nav.domains')}</Button>
          );
        case 'manage_tokens':
          return (
            <PermissionGuard
              permission="Organization.adjustTokens"
              fallback={<span className="text-default-400 text-sm">—</span>}
            >
              <Tooltip content={i18n.t('copy.adjust_event_quota')}>
                <Button
                  color="success"
                  size="sm"
                  variant="flat"
                  onPress={() => {
                    setSelectedOrg(organization);
                    onTokensOpen();
                  }}
                  startContent={<RiCoinsLine className="h-4 w-4" />}
                >
                  Manage Event Quota
                </Button>
              </Tooltip>
            </PermissionGuard>
          );
        case 'actions':
          return (
            <div className="flex items-center justify-end">
              <PermissionGuard permission="Organization.update">
                <Tooltip content={i18n.t('copy.edit_workspace')}>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setSelectedOrg(organization);
                      onEditOpen();
                    }}
                  >
                    <RiEdit2Line className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="Organization.destroy">
                <Tooltip content={i18n.t('copy.delete_workspace')}>
                  <Button
                    isIconOnly
                    color="danger"
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setSelectedOrgId(organization.id);
                      onDeleteOpen();
                    }}
                  >
                    <RiDeleteBinLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [loadMembers, loadDomains, onDeleteOpen, onEditOpen, onTokensOpen]
  );

  const columns = React.useMemo(
    () => [
      { name: i18n.t('copy.workspace'), uid: 'name', sortable: true },
      { name: i18n.t('copy.available_event_quota'), uid: 'token_balance', sortable: false, align: 'center' },
      { name: i18n.t('copy.owner'), uid: 'user', sortable: true },
      { name: i18n.t('columns.createdAt'), uid: 'created_at', sortable: true },
      { name: i18n.t('copy.members'), uid: 'members', sortable: false },
      { name: i18n.t('nav.domains'), uid: 'domains', align: 'center', sortable: false },
      { name: i18n.t('copy.event_quota'), uid: 'manage_tokens', align: 'center', sortable: false },
      { name: i18n.t('common.actions'), uid: 'actions', align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns, [
    'name',
    'token_balance',
    'user',
    'created_at',
    'members',
    'domains',
    'manage_tokens',
    'actions'
  ]);

  const visibleColumnsSet = React.useMemo(
    () => new Set(Array.from(visibleColumns).map((c) => c.toString())),
    [visibleColumns]
  );

  const orgsMeta = data?.meta;

  // Convert EntityFilters to Record<string, string | undefined> for export
  const exportFilters = React.useMemo(() => {
    const result: Record<string, string | undefined> = {};
    Object.entries(params.filters).forEach(([key, value]) => {
      result[key] = value === undefined || value === null ? undefined : String(value);
    });
    return result;
  }, [params.filters]);

  const {
    exportState,
    isExporting,
    needsConfirmation,
    totalRecords,
    handleRequestExport,
    handleConfirmExport,
    handleCancelExport
  } = useTableExport<OrganizationItem>({
    resourceName: 'Workspaces',
    apiPath: '/organization',
    params: {
      page: params.page,
      per_page: params.per_page,
      filters: exportFilters,
      sort: undefined
    },
    meta: orgsMeta
      ? {
          total: orgsMeta.total,
          per_page: orgsMeta.per_page,
          current_page: orgsMeta.current_page,
          last_page: orgsMeta.last_page
        }
      : undefined,
    columns: columns.map((c) => ({ name: c.name, uid: c.uid })),
    visibleColumns: visibleColumnsSet,
    hasAnyFilter: hasActiveFilters,
    mapItemToRow: (org) => ({
      name: org.name,
      token_balance: (org.token_balance ?? 0).toLocaleString() + ' events',
      user: org.user ? `${org.user.first_name} ${org.user.last_name}` : '',
      created_at: org.created_at,
      members: '',
      domains: ''
    })
  });

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedItems}
      emptyMessage={i18n.t('copy.no_workspaces_found')}
      error={error}
      errorMessage={i18n.t('copy.failed_to_load_workspaces')}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasActiveFilters}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
          rightSection={
            <Button
              color="success"
              size="sm"
              variant="flat"
              onPress={handleRequestExport}
              isLoading={isExporting}
              isDisabled={isLoading || !data?.meta?.total}
              startContent={<RiDownloadLine className="h-4 w-4" />}
            >{i18n.t('table.exportCsv')}</Button>
          }
        >
          <SearchInput
            placeholder={i18n.t('copy.search_workspaces')}
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <DateRangeFilter label={i18n.t('filters.createdDate')} value={dateFilter} onChange={handleDateChange} />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title={i18n.t('nav.workspaces')}
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) => setSortDescriptor(d)}
    >
      {needsConfirmation && (
        <ExportConfirmModal
          isOpen={exportState === 'confirm'}
          onConfirm={handleConfirmExport}
          onCancel={handleCancelExport}
          totalRecords={totalRecords}
          resourceLabel="workspace"
        />
      )}
      {/* Members Modal */}
      <MembersModal
        isOpen={isMembersOpen}
        onOpenChange={setIsMembersOpen}
        members={members}
        isLoading={isLoadingMembers}
        organizationName={selectedOrgName}
        organizationId={selectedOrgId === null ? undefined : selectedOrgId}
        onMembersChange={() => {
          // Refresh members when they change
          if (selectedOrgId != null) {
            loadMembers(selectedOrgId, selectedOrgName || '');
          }
        }}
      />

      {/* Edit Workspace Modal */}
      <EditOrganizationModal
        isOpen={isEditOpen}
        onOpenChange={onEditOpenChange}
        organization={selectedOrg}
        onSuccess={triggerRefresh}
      />

      {/* Adjust Event Quota Modal */}
      <AdjustTokensModal
        isOpen={isTokensOpen}
        onOpenChange={onTokensOpenChange}
        organization={selectedOrg}
        onSuccess={triggerRefresh}
      />

      {/* Domains Modal */}
      <DomainsModal
        isOpen={isDomainsOpen}
        onOpenChange={setIsDomainsOpen}
        domains={domains}
        isLoading={isLoadingDomains}
        organizationName={selectedOrgName}
        organizationId={selectedOrgId === null ? undefined : selectedOrgId}
        onDomainsChange={() => {
          // Refresh domains when they change
          if (selectedOrgId != null) {
            loadDomains(selectedOrgId, selectedOrgName || '');
          }
        }}
      />

      {/* Delete Modal */}
      <Modal isOpen={isDeleteOpen} placement="center" onOpenChange={onDeleteOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">{i18n.t('modals.deleteWorkspace.title')}</ModalHeader>
              <ModalBody>
                <p>Are you sure you want to delete this workspace?</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
                <Button color="danger" isLoading={isDeleting} onPress={handleDelete}>{i18n.t('common.delete')}</Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </TablePage>
  );
}
